import { GalleryVertical, List, SlidersHorizontal } from 'lucide-react';
import * as React from 'react';
import { PageHeader } from '@/components/layout/page-header';
import { CarregarMais } from '@/components/patterns/carregar-mais';
import { Segmentado } from '@/components/patterns/segmented';
import { EstadoErroOuIndisponivel, EstadoVazio } from '@/components/patterns/states';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { dataSource } from '@/data';
import { ehAcessoNegado } from '@/data/http';
import type { BuildResumo, Ramo } from '@/data/types';
import { useFeed } from '@/hooks/use-feed';
import { usePodeVer } from '@/hooks/use-acesso';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useResource } from '@/hooks/use-resource';
import { useSessao } from '@/hooks/use-sessao';
import { acoesDeStatus } from '@/lib/capabilities';
import { agruparPorDia, plural } from '@/lib/format';
import { useAcoesRapidas } from '@/features/versao/use-acoes-rapidas';
import { VersaoSheet } from '@/features/versao/VersaoSheet';
import { BarraFiltros } from './BarraFiltros';
import { CardCompacto } from './CardCompacto';
import { PainelFiltros, type OpcoesFiltros } from './PainelFiltros';
import { PublicacaoCard } from './PublicacaoCard';
import { ResumoLateral } from './ResumoLateral';
import { useFiltrosFeed } from './use-filtros-feed';
import { useVista, type Vista } from './use-vista';

/**
 * Feed de versões, em duas vistas sobre os MESMOS filtros (URL), dados e
 * paginação por cursor:
 *
 *  - "Feed" (padrão): coluna única larga, barra de filtros horizontal no topo,
 *    métricas em faixa e cards compactos agrupados por dia;
 *  - "Timeline": duas colunas — linha do tempo de publicações (com "Ver
 *    mudanças") e, ao lado, resumo e filtros em coluna.
 *
 * Celular: uma coluna e os filtros atrás do botão "Filtros" (Sheet) nas duas
 * vistas. A escolha da vista vive em `?vista=` e no localStorage.
 */

const OPCOES_VISTA: Array<{ valor: Vista; rotulo: string; icone: React.ReactNode }> = [
  { valor: 'timeline', rotulo: 'Timeline', icone: <GalleryVertical aria-hidden="true" /> },
  { valor: 'feed', rotulo: 'Feed', icone: <List aria-hidden="true" /> },
];

export function FeedPage({
  ramoFixo,
  cabecalho,
}: {
  ramoFixo?: string;
  /** Recebe o seletor de vista para encaixar no próprio cabeçalho. */
  cabecalho?: (seletorVista: React.ReactNode) => React.ReactNode;
}) {
  const { roles } = useSessao();
  const desktop = useMediaQuery('(min-width: 1024px)');
  const [filtrosAbertos, setFiltrosAbertos] = React.useState(false);
  const [vista, setVista] = useVista();
  const f = useFiltrosFeed(ramoFixo);
  const verRamos = usePodeVer('ramos');

  const feed = useFeed(f.filtros);
  const acoes = useAcoesRapidas();

  const ramos = useResource(
    (signal) =>
      verRamos
        ? dataSource.listarRamos(f.produto ?? undefined, signal).catch((causa: unknown) => {
            if (ehAcessoNegado(causa)) return [] as Ramo[];
            throw causa;
          })
        : Promise.resolve([] as Ramo[]),
    [f.produto, verRamos],
  );
  const opcoesRamo = (ramos.dados ?? []).map((item) => ({ valor: item.ramo, rotulo: item.ramo }));
  const opcoesBranch = React.useMemo(() => {
    const base = (ramos.dados ?? []).filter((item) => !f.ramo || item.ramo === f.ramo);
    const doRamos = base.flatMap((item) => item.branches);
    // Sem a lista de ramos (sem acesso), as branches vêm do que já foi carregado.
    const todas = doRamos.length > 0 ? doRamos : feed.itens.map((item) => item.branch);
    return [...new Set(todas)]
      .sort((a, b) => (a === 'master' ? 1 : b === 'master' ? -1 : b.localeCompare(a)))
      .map((valor) => ({ valor, rotulo: valor }));
  }, [ramos.dados, f.ramo, feed.itens]);

  const opcoes: OpcoesFiltros = {
    ramoFixo: Boolean(ramoFixo),
    mostrarRamo: verRamos,
    opcoesRamo,
    opcoesBranch,
  };

  const grupos = React.useMemo(() => agruparPorDia(feed.itens, (item) => item.publicado_em), [feed.itens]);

  function abrirVersao(build: BuildResumo) {
    f.abrirVersao(build.versao);
  }
  function fecharVersao() {
    f.atualizar({ versao: null });
  }

  const seletor = (
    <Segmentado
      rotulo="Vista do feed"
      valor={vista}
      aoMudar={setVista}
      opcoes={OPCOES_VISTA}
      compacto
    />
  );

  const botaoFiltros = desktop ? null : (
    <div className="flex items-center gap-3">
      <Button variant="outline" onClick={() => setFiltrosAbertos(true)} aria-haspopup="dialog">
        <SlidersHorizontal aria-hidden="true" />
        Filtros
        {f.quantidade > 0 ? (
          <span
            className="inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-label text-on-primary"
            aria-label={plural(f.quantidade, 'filtro ativo', 'filtros ativos')}
          >
            {f.quantidade}
          </span>
        ) : null}
      </Button>
      {f.quantidade > 0 ? (
        <span className="text-cell text-on-surface-muted">{plural(f.quantidade, 'filtro ativo', 'filtros ativos')}</span>
      ) : null}
    </div>
  );

  const compacta = vista === 'feed';

  const corpo = (
    <section aria-label="Publicações" className="min-w-0">
      {/* Anuncia páginas novas e mudanças de filtro para leitor de tela. */}
      <p className="sr-only" role="status" aria-live="polite">
        {feed.estado === 'pronto' ? `${plural(feed.itens.length, 'versão carregada', 'versões carregadas')}.` : ''}
      </p>

      {feed.estado === 'carregando' ? (
        <FeedCarregando compacta={compacta} />
      ) : feed.estado === 'erro' ? (
        <Card>
          <EstadoErroOuIndisponivel erro={feed.erro} aoTentarDeNovo={feed.recarregar} />
        </Card>
      ) : feed.itens.length === 0 ? (
        <Card>
          <EstadoVazio
            titulo={f.quantidade > 0 ? 'Nenhuma versão com estes filtros' : 'Nenhuma versão publicada ainda'}
            descricao={
              f.quantidade > 0
                ? 'Afrouxe a busca, o período ou limpe os filtros para ver mais versões.'
                : 'Assim que o Jenkins publicar um build, ele aparece aqui.'
            }
            acao={
              f.quantidade > 0 ? (
                <Button variant="outline" size="sm" onClick={f.limpar}>
                  Limpar filtros
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {grupos.map((grupo) => (
            <section key={grupo.chave} aria-labelledby={`dia-${grupo.chave}`} className="flex flex-col">
              <div className="sticky top-0 z-10 -mx-1 bg-bg-page px-1 py-2">
                <div className={compacta ? 'flex items-baseline gap-3' : 'relative flex items-baseline gap-3 pl-7 sm:pl-9'}>
                  {compacta ? null : (
                    <span aria-hidden="true" className="absolute left-[3px] top-[8px] size-[9px] rounded-full bg-border-strong" />
                  )}
                  <h2 id={`dia-${grupo.chave}`} className="text-card-title">
                    {grupo.rotulo}
                  </h2>
                  <span className="cmp-eyebrow">{plural(grupo.itens.length, 'versão', 'versões')}</span>
                </div>
              </div>
              <ul
                className={
                  compacta
                    ? 'flex flex-col gap-2'
                    : 'relative before:absolute before:bottom-0 before:left-[7px] before:top-0 before:w-px before:bg-border'
                }
                aria-label={`Publicações de ${grupo.rotulo}`}
              >
                {grupo.itens.map((build) =>
                  compacta ? (
                    <CardCompacto
                      key={build.id}
                      build={build}
                      ativo={build.versao === f.versaoAberta}
                      acoesRapidas={acoesDeStatus(build, roles)}
                      aoAbrir={abrirVersao}
                      aoExecutarAcao={(item, acao) => void acoes.executar(item, acao)}
                      emAcao={acoes.emAcao === build.versao}
                    />
                  ) : (
                    <PublicacaoCard
                      key={build.id}
                      build={build}
                      ativo={build.versao === f.versaoAberta}
                      acoesRapidas={acoesDeStatus(build, roles)}
                      aoAbrir={abrirVersao}
                      aoExecutarAcao={(item, acao) => void acoes.executar(item, acao)}
                      emAcao={acoes.emAcao === build.versao}
                    />
                  ),
                )}
              </ul>
            </section>
          ))}

          <CarregarMais
            temMais={feed.temMais}
            carregando={feed.carregandoMais}
            erro={feed.erroMais}
            aoCarregar={feed.carregarMais}
            total={feed.itens.length}
            singular="versão carregada"
            plural_="versões carregadas"
            fim="Fim do feed"
            rotuloCarregando="Carregando versões…"
            mensagemErro="Não foi possível carregar mais versões"
          />
        </div>
      )}
    </section>
  );

  return (
    <div className={compacta ? 'cmp-page flex flex-col gap-5' : 'cmp-page flex flex-col gap-6 lg:max-w-[70rem]'}>
      {cabecalho ? (
        cabecalho(seletor)
      ) : (
        <PageHeader
          eyebrow="Compilações"
          titulo="Feed de versões"
          descricao="Cada versão do instalador ERP que o Jenkins publica, da mais recente para a mais antiga."
          acao={seletor}
        />
      )}

      {compacta ? (
        <>
          {ramoFixo ? null : <ResumoLateral faixa />}
          {desktop ? <BarraFiltros f={f} opcoes={opcoes} /> : botaoFiltros}
          {corpo}
        </>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,46rem)_20rem] lg:items-start lg:justify-between lg:gap-8">
          {/* Lateral: resumo e filtros (desktop). Sem sticky/overflow: a coluna
              acompanha a rolagem da página inteira e nada é cortado. */}
          <aside aria-label="Resumo e filtros" className="order-first flex flex-col gap-4 lg:order-last">
            {ramoFixo ? null : <ResumoLateral />}
            {desktop ? (
              <Card as="section" aria-label="Filtros" className="flex flex-col gap-5 p-4">
                <div className="flex items-baseline justify-between">
                  <h2 className="text-card-title">Filtros</h2>
                  {f.quantidade > 0 ? <span className="cmp-eyebrow">{plural(f.quantidade, 'ativo', 'ativos')}</span> : null}
                </div>
                <PainelFiltros prefixo="feed" f={f} opcoes={opcoes} />
              </Card>
            ) : null}
          </aside>
          <div className="min-w-0">
            {desktop ? null : <div className="mb-4">{botaoFiltros}</div>}
            {corpo}
          </div>
        </div>
      )}

      {desktop ? null : (
        <Sheet open={filtrosAbertos} onOpenChange={setFiltrosAbertos}>
          <SheetContent side="right" className="w-[min(24rem,100vw)] sm:w-[min(24rem,calc(100vw-3rem))]">
            <div className="flex flex-col gap-1 pr-8">
              <SheetTitle>Filtros</SheetTitle>
              <SheetDescription className="text-cell">Os filtros ficam no endereço da página e podem ser compartilhados.</SheetDescription>
            </div>
            <PainelFiltros prefixo="feed-sheet" f={f} opcoes={opcoes} />
            <Button variant="primary" className="mt-auto" onClick={() => setFiltrosAbertos(false)}>
              Ver versões
            </Button>
          </SheetContent>
        </Sheet>
      )}

      {acoes.dialogos}
      <VersaoSheet versao={f.versaoAberta} aoFechar={fecharVersao} />
    </div>
  );
}

function FeedCarregando({ compacta }: { compacta: boolean }) {
  if (compacta) {
    return (
      <div className="flex flex-col gap-2" role="status" aria-label="Carregando versões">
        <Skeleton className="h-7 w-24" />
        {Array.from({ length: 5 }).map((_, indice) => (
          <Skeleton key={indice} className="h-[5.5rem] rounded-card" />
        ))}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2" role="status" aria-label="Carregando versões">
      <Skeleton className="h-7 w-24" />
      <div className="relative before:absolute before:bottom-0 before:left-[7px] before:top-0 before:w-px before:bg-border">
        {Array.from({ length: 4 }).map((_, indice) => (
          <div key={indice} className="relative pb-4 pl-7 sm:pl-9">
            <span aria-hidden="true" className="absolute left-0 top-[22px] size-[15px] rounded-full border-2 border-border-strong bg-bg-page ring-4 ring-bg-page" />
            <div className="flex flex-col gap-3 rounded-card border border-border bg-bg-surface p-4">
              <Skeleton className="h-4 w-64 max-w-full" />
              <Skeleton className="h-8 w-56" />
              <Skeleton className="h-12" />
              <Skeleton className="h-8 w-72 max-w-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
